/**
 * Complete your profile — the phone number gate.
 *
 * Signing up with email has always required a phone number. Signing in with
 * Google never asked, because Google does not hand one over, so 275 parent
 * accounts reached the roster with no number on them and coaches had no way
 * to reach those families.
 *
 * This blocks the app until the number is given. It is deliberately the same
 * shape as the force-password-change gate: one field, no way around it, and
 * gone for good once answered.
 */

import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import LogoLogin from "../components/LogoLogin";
import { useAuth } from "../context/auth";
import usersService from "../api/services/users.service";

// The dashboards. A `from` pointing at one of these carries no intent —
// it is just where somebody already was — so it is ignored in favour of the
// user's own role home. Matches the rule the login redirect follows.
const ROLE_HOMES = ["/dashboard", "/coachdashboard", "/admin"];

export default function CompleteProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useAuth();

  const [phone, setPhone] = useState(user?.phone || "");
  // Collected on the same screen rather than a second one. Only accounts
  // already being asked for a phone see this, so nobody who is already set
  // up gets chased — which is what the club asked for.
  const [address, setAddress] = useState(user?.address || "");
  const [city, setCity] = useState(user?.city || "");
  const [addressState, setAddressState] = useState(user?.state || "");
  const [zip, setZip] = useState(user?.zip_code || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmed = phone.trim();
    // Ten digits is a US number; we accept whatever separators they type.
    const digits = trimmed.replace(/\D/g, "");
    if (digits.length < 10) {
      setError("Please enter a phone number we can reach you on.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      // The server puts the address into one shape; send what they typed.
      //
      // A blank box is LEFT OUT rather than sent as null. The profile
      // endpoint writes exactly the keys it is given, so posting null would
      // erase an address the family had already given us — which is what
      // would happen to anyone whose cached account details predate this
      // screen. Omitting the key leaves the stored value untouched.
      const payload = { phone: trimmed };
      const optional = {
        address: address.trim(),
        city: city.trim(),
        state: addressState.trim(),
        zip_code: zip.trim(),
      };
      for (const [field, value] of Object.entries(optional)) {
        if (value) payload[field] = value;
      }

      const updated = await usersService.update(payload);
      // Keep the context in step, or the gate below re-fires immediately.
      updateUser(updated);
      toast.success("Thank you — your phone number is saved.");

      const role = user?.role?.toUpperCase();
      const home =
        role === "COACH"
          ? "/coachdashboard"
          : role === "ADMIN" || role === "OWNER"
            ? "/admin"
            : "/dashboard";

      // Go where they were actually headed. A parent following an emailed
      // link to sign a waiver is stopped here first; sending them to the
      // dashboard afterwards loses the errand they came to run.
      const intended = location.state?.from?.pathname;
      const target =
        intended && !ROLE_HOMES.includes(intended) ? intended : home;

      navigate(target, { replace: true });
    } catch (err) {
      console.error("Failed to save phone number:", err);
      setError(
        err.response?.data?.message ||
          "We couldn't save that. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col justify-center items-center bg-page-gradient px-4 py-8">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl px-6 sm:px-8 py-8">
        <div className="flex justify-center mb-2">
          <LogoLogin />
        </div>

        <h1 className="text-2xl font-semibold text-[#0f172a] text-center font-kollektif">
          One last thing
        </h1>
        <p className="text-gray-600 text-center mt-2 font-manrope">
          We need a phone number for your account so your child&rsquo;s coach
          can reach you.
        </p>

        <form onSubmit={handleSubmit} className="mt-6">
          <label
            htmlFor="profile-phone"
            className="block text-sm font-medium text-gray-700 font-manrope mb-2"
          >
            Phone number
          </label>
          <input
            id="profile-phone"
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            autoFocus
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              if (error) setError("");
            }}
            placeholder="(803) 555-0142"
            className={`w-full p-3 border rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-[#173151] font-manrope ${
              error ? "border-red-400" : "border-gray-300"
            }`}
          />
          {error && (
            <p className="text-sm text-red-600 mt-2 font-manrope">{error}</p>
          )}

          <div className="mt-5 pt-4 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 font-manrope mb-3">
              Address{" "}
              <span className="font-normal text-gray-500">
                (optional, used for posting jerseys)
              </span>
            </p>

            <label htmlFor="profile-address" className="sr-only">
              Street address
            </label>
            <input
              id="profile-address"
              name="address"
              autoComplete="street-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Street address"
              className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-[#173151] font-manrope"
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
              <div className="sm:col-span-1">
                <label htmlFor="profile-city" className="sr-only">
                  City
                </label>
                <input
                  id="profile-city"
                  name="city"
                  autoComplete="address-level2"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-[#173151] font-manrope"
                />
              </div>
              <div>
                <label htmlFor="profile-state" className="sr-only">
                  State
                </label>
                <input
                  id="profile-state"
                  name="state"
                  autoComplete="address-level1"
                  value={addressState}
                  onChange={(e) => setAddressState(e.target.value)}
                  placeholder="NC"
                  className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-[#173151] font-manrope"
                />
              </div>
              <div>
                <label htmlFor="profile-zip" className="sr-only">
                  ZIP code
                </label>
                <input
                  id="profile-zip"
                  name="zip_code"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="ZIP"
                  className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-[#173151] font-manrope"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full mt-5 bg-[#173151] text-white font-semibold rounded-xl py-3 shadow-md hover:bg-[#1f3d67] disabled:opacity-50 font-manrope"
          >
            {saving ? "Saving..." : "Save and continue"}
          </button>
        </form>

        <p className="text-xs text-gray-500 text-center mt-4 font-manrope">
          We only use this to contact you about your child&rsquo;s classes.
        </p>
      </div>
    </div>
  );
}
